import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Settings,
  Brain,
  Key,
  Save,
  RefreshCw,
  Zap,
  Check,
  AlertCircle,
  BookA,
  History,
  Calendar,
  Search,
} from "lucide-react";

const ConfigPage = () => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiConfigs, setAiConfigs] = useState([]);
  const [activeConfig, setActiveConfig] = useState(null);
  const [newConfig, setNewConfig] = useState({
    name: "Configuración de Análisis",
    prompt_template: `Analiza la siguiente transcripción de una llamada de call center y proporciona:
1. Sentimiento general (escala 1-10 donde 1 es muy negativo y 10 es muy positivo)
2. Sentimiento de apertura
3. Sentimiento de cierre
4. Temas detectados
5. Palabras clave
6. Resumen de la llamada
7. Puntuación de riesgo (0-100)

Transcripción:
{transcription}

Responde en formato JSON con las siguientes claves:
- general_sentiment (número)
- opening_sentiment (número)
- closing_sentiment (número)
- topics (lista de strings)
- keywords (lista de objetos con "word" y "count")
- summary (string)
- risk_score (número)
- risk_factors (lista de strings)`,
    model: "gpt-4o",
    temperature: 0.3,
    max_tokens: 2000,
  });

  const [dictionary, setDictionary] = useState({ name: "Diccionario Principal", keywords: "", topics: "" });
  const [retroSearch, setRetroSearch] = useState({ date_from: "", date_to: "" });
  const [runningBatch, setRunningBatch] = useState(false);

  const fetchConfigs = async () => {
    try {
      const response = await authAxios.get("/ai-config");
      setAiConfigs(response.data);
      const active = response.data.find(c => c.is_active);
      if (active) {
        setActiveConfig(active);
        setNewConfig(active);
      }
    } catch (error) {
      console.error("Error fetching AI configs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await authAxios.post("/ai-config", newConfig);
      toast.success("Configuración guardada y activada");
      fetchConfigs();
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleActivateConfig = async (configId) => {
    try {
      await authAxios.put(`/ai-config/${configId}/activate`);
      toast.success("Configuración activada");
      fetchConfigs();
    } catch (error) {
      toast.error("Error al activar configuración");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Configuración</h1>
        <p className="text-slate-500 mt-1">
          Configura los parámetros de análisis con IA
        </p>
      </div>

      <Tabs defaultValue="ai" className="space-y-6">
        <TabsList className="bg-slate-100 mb-6">
          <TabsTrigger value="ai" className="flex items-center gap-2">
            <Brain className="w-4 h-4" />
            Configuración IA
          </TabsTrigger>
          <TabsTrigger value="dictionaries" className="flex items-center gap-2">
            <BookA className="w-4 h-4" />
            Diccionarios y Temas
          </TabsTrigger>
          <TabsTrigger value="retroactive" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Búsqueda Retroactiva
          </TabsTrigger>
          <TabsTrigger value="integration" className="flex items-center gap-2">
            <Key className="w-4 h-4" />
            Integración
          </TabsTrigger>
        </TabsList>

        {/* AI Configuration Tab */}
        <TabsContent value="ai" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Config Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="w-5 h-5 text-sky-600" />
                    Configuración del Análisis
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label htmlFor="config-name">Nombre de la Configuración</Label>
                    <Input
                      id="config-name"
                      value={newConfig.name}
                      onChange={(e) => setNewConfig({ ...newConfig, name: e.target.value })}
                      placeholder="Ej: Análisis de Servicio al Cliente"
                      className="mt-1"
                      data-testid="config-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="provider">Proveedor IA</Label>
                      <Select
                        value={newConfig.provider || "openai"}
                        onValueChange={(v) => {
                          const defaultModel = v === "gemini" ? "gemini-1.5-flash" : "gpt-4o";
                          setNewConfig({ ...newConfig, provider: v, model: defaultModel });
                        }}
                      >
                        <SelectTrigger className="mt-1" data-testid="provider-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="openai">OpenAI</SelectItem>
                          <SelectItem value="gemini">Google Gemini</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="model">Modelo de IA</Label>
                      <Select
                        value={newConfig.model || "gpt-4o"}
                        onValueChange={(v) => setNewConfig({ ...newConfig, model: v })}
                      >
                        <SelectTrigger className="mt-1" data-testid="model-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {newConfig.provider === "gemini" ? (
                            <>
                              <SelectItem value="gemini-1.5-flash">Gemini 1.5 Flash</SelectItem>
                              <SelectItem value="gemini-1.5-pro">Gemini 1.5 Pro</SelectItem>
                            </>
                          ) : (
                            <>
                              <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                              <SelectItem value="gpt-4o-mini">GPT-4o Mini</SelectItem>
                              <SelectItem value="gpt-4-turbo">GPT-4 Turbo</SelectItem>
                              <SelectItem value="gpt-5-mini">GPT-5 Mini</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="temperature">Temperatura ({newConfig.temperature})</Label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={newConfig.temperature}
                        onChange={(e) => setNewConfig({ ...newConfig, temperature: parseFloat(e.target.value) })}
                        className="w-full mt-3 accent-sky-600"
                        data-testid="temperature-slider"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Menor = más consistente, Mayor = más creativo
                      </p>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="max-tokens">Tokens Máximos</Label>
                    <Input
                      id="max-tokens"
                      type="number"
                      value={newConfig.max_tokens}
                      onChange={(e) => setNewConfig({ ...newConfig, max_tokens: parseInt(e.target.value) })}
                      min={500}
                      max={4000}
                      className="mt-1"
                      data-testid="max-tokens-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="prompt">Prompt de Análisis (Template)</Label>
                    <Textarea
                      id="prompt"
                      value={newConfig.prompt_template}
                      onChange={(e) => setNewConfig({ ...newConfig, prompt_template: e.target.value })}
                      rows={15}
                      className="mt-1 font-mono text-sm"
                      placeholder="Define el prompt para el análisis..."
                      data-testid="prompt-textarea"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Usa {"{transcription}"} como placeholder para la transcripción de la llamada
                    </p>
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (activeConfig) setNewConfig(activeConfig);
                      }}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Restaurar
                    </Button>
                    <Button
                      onClick={handleSaveConfig}
                      disabled={saving}
                      className="bg-sky-600 hover:bg-sky-700"
                      data-testid="save-config-btn"
                    >
                      {saving ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Guardar y Activar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Saved Configs */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Configuraciones Guardadas</CardTitle>
                </CardHeader>
                <CardContent>
                  {aiConfigs.length === 0 ? (
                    <p className="text-slate-500 text-sm text-center py-4">
                      No hay configuraciones guardadas
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {aiConfigs.map((config) => (
                        <div
                          key={config.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            config.is_active
                              ? "border-sky-300 bg-sky-50"
                              : "border-slate-200 hover:border-sky-200"
                          }`}
                          onClick={() => setNewConfig(config)}
                          data-testid={`config-item-${config.id}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-sm truncate">{config.name}</span>
                            {config.is_active && (
                              <Badge className="bg-green-100 text-green-700">
                                <Check className="w-3 h-3 mr-1" />
                                Activa
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span>{config.model}</span>
                            <span>•</span>
                            <span>T: {config.temperature}</span>
                          </div>
                          {!config.is_active && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full mt-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleActivateConfig(config.id);
                              }}
                            >
                              Activar
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Tips Card */}
              <Card className="mt-4 bg-sky-50 border-sky-100">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sky-800">Consejos</p>
                      <ul className="text-sm text-sky-700 mt-2 space-y-1">
                        <li>• Usa temperatura baja (0.2-0.4) para análisis consistentes</li>
                        <li>• GPT-4o ofrece mejor comprensión del contexto</li>
                        <li>• El formato JSON facilita la extracción de datos</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Dictionaries Tab */}
        <TabsContent value="dictionaries">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookA className="w-5 h-5 text-sky-600" />
                Diccionario de Palabras y Temas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="dict-name">Nombre del Diccionario</Label>
                <Input
                  id="dict-name"
                  value={dictionary.name}
                  onChange={(e) => setDictionary({...dictionary, name: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="dict-keywords">Palabras Clave (Separadas por comas)</Label>
                  <Textarea
                    id="dict-keywords"
                    value={dictionary.keywords}
                    onChange={(e) => setDictionary({...dictionary, keywords: e.target.value})}
                    placeholder="promoción, cancelar, urgente, enojo, satisfecho..."
                    rows={6}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    La IA priorizará detectar y contabilizar estas palabras en la transcripción.
                  </p>
                </div>
                <div>
                  <Label htmlFor="dict-topics">Temas de Negocio Esperados (Separados por comas)</Label>
                  <Textarea
                    id="dict-topics"
                    value={dictionary.topics}
                    onChange={(e) => setDictionary({...dictionary, topics: e.target.value})}
                    placeholder="Soporte Técnico, Facturación, Quejas, Retención..."
                    rows={6}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Fuerza al modelo a clasificar las llamadas dentro de estos temas específicos.
                  </p>
                </div>
              </div>
              <div className="flex justify-end pt-4 border-t">
                <Button 
                  className="bg-sky-600 hover:bg-sky-700"
                  onClick={async () => {
                    try {
                      await authAxios.post("/config/dictionaries", {
                        name: dictionary.name,
                        keywords: dictionary.keywords.split(",").map(k => k.trim()).filter(Boolean),
                        topics: dictionary.topics.split(",").map(t => t.trim()).filter(Boolean)
                      });
                      toast.success("Diccionario guardado correctamente");
                    } catch (e) {
                      toast.error("Error al guardar diccionario");
                    }
                  }}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Guardar Diccionario
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Retroactive Search Tab */}
        <TabsContent value="retroactive">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-indigo-600" />
                Búsqueda y Re-Análisis Retroactivo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-lg text-indigo-800 text-sm">
                Utiliza esta herramienta si has actualizado tus Diccionarios de Palabras o la Configuración de IA y necesitas volver a analizar llamadas pasadas para que reflejen los nuevos parámetros.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="date-from">Desde la Fecha</Label>
                  <div className="relative mt-1">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="date-from"
                      type="date"
                      value={retroSearch.date_from}
                      onChange={(e) => setRetroSearch({...retroSearch, date_from: e.target.value})}
                      className="pl-9"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="date-to">Hasta la Fecha</Label>
                  <div className="relative mt-1">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="date-to"
                      type="date"
                      value={retroSearch.date_to}
                      onChange={(e) => setRetroSearch({...retroSearch, date_to: e.target.value})}
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end pt-4 border-t">
                <Button 
                  className="bg-indigo-600 hover:bg-indigo-700"
                  disabled={runningBatch || !retroSearch.date_from || !retroSearch.date_to}
                  onClick={async () => {
                    setRunningBatch(true);
                    try {
                      const res = await authAxios.post("/calls/reanalyze-batch", retroSearch);
                      toast.success(res.data.message || "Proceso por lotes iniciado");
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Error al iniciar re-análisis");
                    } finally {
                      setRunningBatch(false);
                    }
                  }}
                >
                  {runningBatch ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Iniciar Re-Análisis
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integration Tab */}
        <TabsContent value="integration">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* uContact */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-orange-500" />
                  uContact
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <span className="text-green-700 font-medium">Conectado</span>
                  </div>
                  <Badge className="bg-green-100 text-green-700">Activo</Badge>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">URL Base</span>
                    <span className="font-mono text-slate-700">cpx.ucontactcloud.com</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Usuario</span>
                    <span className="text-slate-700">CarlosLopez</span>
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  La configuración de uContact se gestiona desde las variables de entorno del servidor.
                </p>
              </CardContent>
            </Card>

            {/* OpenAI */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-green-500" />
                  OpenAI
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <span className="text-green-700 font-medium">API Key Configurada</span>
                  </div>
                  <Badge className="bg-green-100 text-green-700">Activo</Badge>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">API Key</span>
                    <span className="font-mono text-slate-700">sk-proj-...4A</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Modelos</span>
                    <span className="text-slate-700">GPT-4o, Whisper</span>
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  Se utiliza para transcripción de audio y análisis de sentimiento.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ConfigPage;
